import React from 'react';

import { ScreenContainerSafeAreaProvider } from '@components/ScreenContainer';
import type { FollowUsersScreenType } from '@navigation/FollowUsersStack';
import type { GroupScreenType } from '@navigation/GroupStack';
import { OnboardingRoute } from '@navigation/Routes';
import type { FollowUsersScreenProps, ViewGroupsScreenProps } from '@navigation/Types';
import { StackScreenProps, createStackNavigator } from '@react-navigation/stack';
import ViewGroupsScreen from '@screens/Group/List';
import RequestPermissionsScreen from '@screens/RequestPermissions';
import FollowUsersScreen from '@screens/User/FollowUsers';
import { CreateUserScreen } from '@screens/User/Form';
import UserPrivateFormScreen from '@screens/User/PrivateForm';
import WelcomeScreen from '@screens/Welcome';

export type OnboardingStackParamList = {
  [OnboardingRoute.CreateUser]: undefined;
  [OnboardingRoute.FollowUsers]: {
    screenType?: FollowUsersScreenType;
  };
  [OnboardingRoute.RequestPermissions]: undefined;
  [OnboardingRoute.Welcome]: undefined;
  [OnboardingRoute.UserPrivateForm]: undefined;
  [OnboardingRoute.JoinGroups]: {
    screenType?: GroupScreenType;
  };
};

type OnboardingStackProps = {
  initialRouteName?: keyof OnboardingStackParamList;
};

const Stack = createStackNavigator<OnboardingStackParamList>();

export default function OnboardingStack({
  initialRouteName = OnboardingRoute.CreateUser,
}: OnboardingStackProps) {
  return (
    <ScreenContainerSafeAreaProvider>
      <Stack.Navigator
        id={undefined}
        initialRouteName={initialRouteName}
        screenOptions={{
          headerShown: false,
        }}>
        <Stack.Screen name={OnboardingRoute.CreateUser} component={CreateUserScreen} />
        <Stack.Screen name={OnboardingRoute.UserPrivateForm} component={UserPrivateFormScreen} />
        <Stack.Screen name={OnboardingRoute.Welcome} component={WelcomeScreen} />
        <Stack.Screen
          name={OnboardingRoute.FollowUsers}
          component={OnboardingFollowUsersScreen}
          initialParams={{ screenType: 'onboarding' }}
        />
        <Stack.Screen
          name={OnboardingRoute.RequestPermissions}
          component={RequestPermissionsScreen}
        />
        <Stack.Screen
          name={OnboardingRoute.JoinGroups}
          component={OnboardingViewGroupsScreen}
          initialParams={{ screenType: 'onboarding' }}
        />
      </Stack.Navigator>
    </ScreenContainerSafeAreaProvider>
  );
}

// These screens are shared with tab stacks; onboarding only uses their onboarding-safe actions.
function OnboardingFollowUsersScreen(
  props: StackScreenProps<OnboardingStackParamList, OnboardingRoute.FollowUsers>
) {
  return <FollowUsersScreen {...(props as unknown as FollowUsersScreenProps)} />;
}

function OnboardingViewGroupsScreen(
  props: StackScreenProps<OnboardingStackParamList, OnboardingRoute.JoinGroups>
) {
  return <ViewGroupsScreen {...(props as unknown as ViewGroupsScreenProps)} />;
}
